import { useEffect } from 'react';
import gql from 'graphql-tag';
import { useMutation, useQuery } from 'react-apollo';
import { useWizardContext } from 'pages/qualify/provider';
import { Inputs, Organization, StepIdsEnum } from 'pages/qualify/types';
import { parseAnyListToOptionsList } from 'pages/qualify/utils';
import { WIZARD_IMAGES_PER_STEP_ID } from 'pages/qualify/wizard-images-urls';
import WizardStep from '../wizard-step';
import WizardInput from '../wizard-input';

export default function GiveawaysAndCouponsStep() {
  const { tokenData, watch, setValues, values, token, organizationData } =
    useWizardContext();
  const {
    data: applicationAssetQueryData,
    loading,
    refetch,
  } = useQuery<{
    sponsorshipApplicationView: Partial<Organization>;
  }>(APPLICATION_WARM_UPS_ASSETS_QUERY, {
    variables: { season: tokenData.season, year: tokenData.year },
    context: { headers: { Authorization: `Bearer ${token}` } },
    fetchPolicy: 'network-only',
  });
  const [performMutation] = useMutation(MUTATION);
  const accepted = watch('accepted');

  const modifyInputsBeforeMutation = (
    data: Partial<Inputs>,
  ): Partial<Inputs> => ({
    ...data,
    quantity: Number(data.quantity),
  });

  useEffect(() => {
    if (values.organizationId) {
      const { savedStepId } = organizationData?.sponsorshipApplication;
      if (savedStepId === StepIdsEnum.COUPONS) return;
      const { couponDetail } =
        applicationAssetQueryData?.sponsorshipApplicationView
          .sponsorshipApplication ?? {};
      setValues({
        ...values,
        accepted:
          applicationAssetQueryData?.sponsorshipApplicationView
            .sponsorshipApplication?.couponAccepted ?? false,
        quantity: couponDetail?.capabilityDetailable?.quantity,
        sponsorablePropertyIds:
          couponDetail?.associatedSponsorableProperties?.map(
            (property) => property.id,
          ),
        refusalReason:
          applicationAssetQueryData?.sponsorshipApplicationView
            .sponsorshipApplication?.couponRefusalReason,
      });
    }
  }, [values.organizationId, applicationAssetQueryData]);

  return (
    <WizardStep
      performMutation={performMutation}
      refetchAfterMutation={refetch}
      mutationStaticIds={{
        organizationId: tokenData.id,
        sponsorshipApplicationId:
          applicationAssetQueryData?.sponsorshipApplicationView
            ?.sponsorshipApplication?.id,
        offerId: tokenData.offer_id,
      }}
      modifyInputsBeforeMutation={modifyInputsBeforeMutation}
      isLoading={loading}
    >
      <WizardStep.Picture
        src={WIZARD_IMAGES_PER_STEP_ID[StepIdsEnum.COUPONS]}
      />
      <WizardStep.Content title="Product and coupon distribution">
        <div className="sui-grid" style={{ gap: 24 }}>
          <WizardInput
            type="boolean-question"
            name="accepted"
            label="My organization is capable of distributing products and/or coupons on behalf of a sponsor"
            helpText="Average sponsorship funding $100-$170"
          />
          {accepted && (
            <>
              <WizardInput
                type="number"
                name="quantity"
                label="Estimated number of products and/or coupons your organization can distribute throughout a season"
                rules={{ required: 'Please enter a quantity' }}
              />
              <WizardInput
                type="select"
                isMulti
                name="sponsorablePropertyIds"
                label="Programs this applies to"
                rules={{ required: 'Please select at least one program' }}
                options={parseAnyListToOptionsList(
                  values.sponsorableProperties ?? [],
                  { withEmptyOption: false },
                )}
              />
            </>
          )}
          {!accepted && (
            <>
              <WizardInput
                type="textarea"
                name="refusalReason"
                label="If possible, please share your reasons and specify the amount of funding at which you might reconsider opting in to this asset"
              />
            </>
          )}
        </div>
      </WizardStep.Content>
    </WizardStep>
  );
}

const APPLICATION_WARM_UPS_ASSETS_QUERY = gql`
  query SponsorshipApplicationView($season: String!, $year: Int!) {
    sponsorshipApplicationView {
      id
      sponsorshipApplication(season: $season, year: $year) {
        id
        couponAccepted
        couponRefusalReason
        couponDetail {
          associatedSponsorableProperties {
            id
            name
          }
          capabilityDetailable {
            ... on Coupon {
              id
              quantity
            }
          }
        }
      }
    }
  }
`;

const MUTATION = gql`
  mutation upsertGiveawayAndCoupon($input: UpsertGiveawayAndCouponInput!) {
    upsertGiveawayAndCoupon(input: $input) {
      id
    }
  }
`;
